import { createContext, useContext } from "react";

/**
 * Result of an attempted passkey sign-in.
 * - `signed-in`: authentication succeeded; the session is being established.
 * - `use-password`: passkey is unavailable for this account or the user dismissed
 *   the WebAuthn prompt; the caller should fall back to the password step.
 * - `error`: an unexpected failure occurred and was already reported to the user.
 */
export type PasskeySignInOutcome = "signed-in" | "use-password" | "error";

/**
 * Result of a password sign-in attempt.
 * - `signed-in`: the device was trusted; the session is being established.
 * - `code-sent`: a 2FA code was emailed; the caller moves to the code step.
 * - `invalid-credentials`: the platform refused the email/password pair before
 *   sending any code; the caller shows the error on the password field.
 * - `too-many-attempts`: the platform throttled this email (password failures
 *   or code sends); `retryAfterSeconds` is the lockout when the platform sends it.
 * - `error`: an unexpected failure occurred and was already reported to the user.
 */
export type SignInOutcome =
  | { status: "signed-in" }
  | { status: "code-sent" }
  | { status: "invalid-credentials" }
  | { status: "too-many-attempts"; retryAfterSeconds: number | null }
  | { status: "error" };

export type LoginContextType = {
  // True while a session is being established or re-read: the screens give way
  // to a loader.
  isBusy: boolean;
  email: string | null;
  setEmail(email: string): void;
  // Kept in memory between the password step and the code step only.
  password: string | null;
  googleClientId?: string;
  signIn(email: string, password: string): Promise<SignInOutcome>;
  signInWithPasskey(email: string): Promise<PasskeySignInOutcome>;
  send2faCode(code: string, trust: boolean): Promise<"signed-in" | "error">;
  signInWithPat(_: { pat: string; profile: string }): Promise<void>;
  googleSignIn(_: { credential: string }): Promise<void>;
  forgotPassword(_: { email: string }): Promise<void>;
  resetPassword(_: {
    email: string;
    code: string;
    password: string;
  }): Promise<void>;
  // Signs out and waits for the session to be re-read, so the caller only
  // resumes once `isLoggedIn` is settled.
  signOut(): Promise<void>;
};

export const LoginContext = createContext<LoginContextType | null>(null);

export function useLogin(): LoginContextType {
  const context = useContext(LoginContext);
  if (!context) {
    throw new Error("useLogin must be used within the auth app AuthProvider");
  }
  return context;
}
